import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpGlobalMessageService, FeatureTogglesService, SessionStorageService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {B2BUser, EventService, GlobalMessageType, TranslationService, User} from '@spartacus/core';
import {Address} from '@spartacus/core/src/model/address.model';
import {B2BUnit} from '@spartacus/core/src/model/org-unit.model';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {BehaviorSubject, combineLatest, Observable, throwError} from 'rxjs';
import {catchError, first, map, take} from 'rxjs/operators';
import {DynamicHeadingService} from '../layout/header/dynamic-heading/dynamic-heading.service';
import {TemplateView} from '../model/template-view.model';
import {EddlCheckoutStartEvent} from '../services/analytics/EDDL/events/eddl-checkout-start-event';
import {B2bUnitService} from '../services/b2b-unit.service';
import {DcpUserService} from '../services/user/dcp-user.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {isPlatformBrowser} from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'dcp-digital-native-checkout',
  templateUrl: './digital-native-checkout.component.html',
  styleUrls: ['./digital-native-checkout.component.scss']
})
export class DigitalNativeCheckoutComponent implements OnInit, OnDestroy {
  public view = TemplateView;
  public b2bUnit$: Observable<B2BUnit>;
  public b2bUnitAddresses$: Observable<Address[]>;
  public b2bUnitContacts$: Observable<B2BUser[]>;
  public currentUser: User;
  public isCurrentUserIndividual: boolean;
  public isApiCallFailed: boolean = false;
  public taxValidationSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public isTaxValidationSkipped$: Observable<boolean>;
  public backUrl: string;
  backUrlHandler: (event: PopStateEvent) => void;

  constructor(@Inject(PLATFORM_ID) public platformId: string,
              public dynamicHeadingService: DynamicHeadingService,
              public translationService: TranslationService,
              public dcpGlobalMessageService: DcpGlobalMessageService,
              private userFacade: UserAccountFacade,
              private b2bUnitService: B2bUnitService,
              private dcpUserService: DcpUserService,
              private cdr: ChangeDetectorRef,
              private eventService: EventService,
              private featureToggleService: FeatureTogglesService,
              private dcpActiveCartService: DcpActiveCartService,
              private dcpConfigService: DcpConfigService,
              private sessionStorageService: SessionStorageService,
  ) {
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.translationService.translate('checkout.digitalNativeCheckout.header').pipe(take(1), untilDestroyed(this)).subscribe((heading) => {
        this.dynamicHeadingService.setHeading(heading);
      });
    });
    this.getInvoiceAddressesAndContacts();
    this.subscribeToMaintenanceDisclaimer();
    this.isCurrentUserIndividual = this.dcpUserService.isUserCompanyTypeIndividual(this.currentUser);
    this.isTaxValidationSkipped();
    this.showBackButton();
    setTimeout(() => this.eventService.dispatch(new EddlCheckoutStartEvent()));
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('popstate', this.backUrlHandler);
    }
  }

  subscribeToMaintenanceDisclaimer(): void {
    combineLatest([
      this.featureToggleService.getToggleWithVariant(UNLEASH_TOGGLES.maintenanceDisclaimer),
      this.dcpActiveCartService.getActiveCart()
    ]).pipe(untilDestroyed(this)).subscribe(([featureToggle, cart]) => {
      if (featureToggle) {
        const {startDate, endDate, countries} = JSON.parse(featureToggle.variant.payload.value);
        const userCountry: string = JSON.parse(cart.entries[0].data.find(e => e.type === 'salesArea').value).country;

        // if country is listed in FT payload then display disclaimer message
        if (countries && countries.find(e => e.toUpperCase() === userCountry)) {
          this.translationService.translate('checkout.digitalNativeCheckout.paymentType.cc.maintenance', {
            startDate,
            endDate
          }).pipe(take(1), untilDestroyed(this)).subscribe(message => {
            this.dcpGlobalMessageService.addMessage({
              message,
              type: GlobalMessageType.MSG_TYPE_INFO,
              skipLogging: true,
            }, this.constructor.name, 'errorGlobal');
          });
        }
      }
    });
  }

  getInvoiceAddressesAndContacts() {
    this.userFacade.get()
      .pipe(first((user: User) => Boolean(user?.uid && user.orgUnit?.uid)), untilDestroyed(this))
      .subscribe((user: User) => {
        this.currentUser = user;
        this.b2bUnit$ = this.b2bUnitService.getB2bUnitByUserAndB2bUnitId(user.uid, user.orgUnit.uid).pipe(catchError((e) => this.genericErrorHandler(e)));
        this.b2bUnitAddresses$ = this.b2bUnitService.getOrgUnitAddresses(user.uid, user.orgUnit.uid).pipe(catchError((e) => this.genericErrorHandler(e)));
        this.b2bUnitContacts$ = this.b2bUnit$.pipe(
          map(b2bUnit => b2bUnit.customers),
          catchError((e) => this.genericErrorHandler(e))
        );
        this.cdr.detectChanges();
      });
  }

  onB2bUnitUpdateCallFailed(): void {
    this.isApiCallFailed = true;
    this.cdr.detectChanges();
  }

  isTaxValidationSkipped(): void {
    this.dcpActiveCartService.getActiveCart()
      .pipe(untilDestroyed(this))
      .subscribe(cart => {
        cart.entries?.some(entry => {
          this.isTaxValidationSkipped$ = this.dcpConfigService.getCommercialModelConfig(entry.product, entry.transactionType)
            .pipe(
              map(commercialModelConfig => commercialModelConfig.skipTaxIdValidation)
            );
        });
      });
  }

  private genericErrorHandler(e) {
    this.isApiCallFailed = true;
    this.cdr.detectChanges();
    return throwError(e);
  };

  showBackButton() {
    const hideHeader = this.sessionStorageService.getItem('hideHeader');
    const url = this.sessionStorageService.getItem('backUrl');
    if (!hideHeader && url) {
      this.backUrl = url;
      this.cdr.detectChanges();

      if (isPlatformBrowser(this.platformId)) {

        this.backUrlHandler = (event) => {
          history.pushState(null, '', window.location.href);
          window.location.href = this.backUrl;
        };
        window.addEventListener('popstate', this.backUrlHandler);
      }
    }
  }


  onBackClick(): void {
    window.location.href = this.backUrl;
  }
}
